import type { AuditAction, AuditEvent } from "../hooks/useAuditEvents";
import { formatDateTime } from "../utils/formatDateTime";

// Pure presentational helpers for an audit event, factored out of AuditTrail so
// both the platform-wide page and the per-instance panel render rows identically
// and the label/detail/relative logic stays unit-testable.

const ACTION_LABEL: Record<AuditAction, string> = {
  USER_PROFILE_UPDATED: "Profile updated",
  CLAW_CREATED: "Created",
  CLAW_DELETED: "Deleted",
  CLAW_REASSIGNED: "Reassigned",
  CLAW_UNLOCKED: "Unlocked",
  CLAW_LLM_TOKEN_SET: "LLM token set",
  CLAW_GATEWAY_PASSWORD_SET: "Gateway password set",
  CLAW_CONTROL_UI_IDENTITY_SET: "Control UI identity set",
  CLAW_AI_MODE_SWITCHED: "AI mode switched",
};

export const labelFor = (action: AuditEvent["action"]): string =>
  ACTION_LABEL[action] ?? action.replace(/^CLAW_/, "").replace(/_/g, " ").toLowerCase();

export const formatAbsolute = (iso: string): string => formatDateTime(iso) || iso;

export const formatRelative = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const diffMs = Date.now() - d.getTime();
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
};

export const detailFor = (event: AuditEvent): string | null => {
  if (event.action === "CLAW_CREATED" && event.tier) return `tier: ${event.tier}`;
  if (event.action === "CLAW_DELETED" && event.instanceId) return `instance: ${event.instanceId}`;
  return null;
};
